import { Box, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/core-utils';

import { useProjectOverview } from './useProjectOverview';
import { FeedbackSnackbar } from '../../../admin-common/components/FeedbackSnackbar';
import { PeopleSection } from '../../../admin-common/components/PeopleSection';
import { useEditProject } from '../../context/EditProjectContext';
import type { ReallocationOperation } from '../../context/editProjectReducer';
import { AllocationSummaryTable } from '../ProjectOverviewContent/AllocationSummaryTable';
import { DatasetSummary } from '../ProjectOverviewContent/DatasetSummary';
import { ProjectBasics } from '../ProjectOverviewContent/ProjectBasics';
import { ReviewConceptAndAttributes } from '../ProjectOverviewContent/ReviewConceptAndAttributes';
import { REALLOCATION_ORDERED_MOVABLE_COUNT_FF } from '../ReallocationWizard/reallocation.utils';
import { ReallocationWizard } from '../ReallocationWizard/ReallocationWizard';
import { BATCH_LABEL_IDS } from '../ReallocationWizard/types';

export const ProjectOverview = () => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const [isReallocationWizardOpen, setReallocationWizardOpen] = useState(false);
  const [confirmedToast, setConfirmedToast] = useState<string | null>(null);
  const toastFixEnabled = Boolean(useFeatureFlagSplit(REALLOCATION_ORDERED_MOVABLE_COUNT_FF));
  const {
    projectName,
    conditionAreaName,
    healthSystemName,
    annotatorNames,
    supervisorNames,
    annotators,
    supervisors,
    addedConcepts,
    validationEncounterCount,
    testEncounterCount,
    trainingEncounterCount,
    allocationSummary,
    isDataLoading,
  } = useProjectOverview();
  const {
    editMode,
    projectId,
    addedAnnotators,
    addedSupervisors,
    addAnnotator,
    removeAnnotator,
    addSupervisor,
    removeSupervisor,
    commitReallocations,
  } = useEditProject();

  const handleConfirmReallocation = (operations: ReallocationOperation[]) => {
    commitReallocations(operations);
    setReallocationWizardOpen(false);
    const batchLabels = [...new Set(operations.map((op) => op.batch))].map((batch) =>
      formatMessage({ id: BATCH_LABEL_IDS[batch] })
    );
    if (batchLabels.length) {
      const messageKey = toastFixEnabled
        ? batchLabels.length > 1
          ? 'adminProjects.reallocation.confirmedToast'
          : 'adminProjects.reallocation.confirmedToastSingle'
        : batchLabels.length > 1
        ? 'adminProjects.reallocation.confirmedToast.legacy'
        : 'adminProjects.reallocation.confirmedToastSingle.legacy';
      setConfirmedToast(formatMessage({ id: messageKey }, { batches: batchLabels.join(', ') }));
    }
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
      <Box>
        <Typography variant="h5">
          {formatMessage({ id: 'adminProjects.projectOverview.title' })}
        </Typography>
      </Box>
      {/* <Alert severity="info" sx={{ mt: 2 , border: `1px solid ${theme.palette.info.main}`}}>
                {formatMessage({ id: 'adminProjects.projectOverview.alert.description' })}
            </Alert> */}
      <Box>
        <ProjectBasics
          projectName={projectName}
          conditionAreaName={conditionAreaName}
          healthSystemName={healthSystemName}
        />
      </Box>
      <Box>
        <PeopleSection
          annotatorNames={annotatorNames}
          supervisorNames={supervisorNames}
          isLoading={isDataLoading}
          editable={editMode}
          persistedAnnotators={annotators}
          persistedSupervisors={supervisors}
          addedAnnotators={addedAnnotators}
          addedSupervisors={addedSupervisors}
          onAddAnnotator={addAnnotator}
          onRemoveAnnotator={removeAnnotator}
          onAddSupervisor={addSupervisor}
          onRemoveSupervisor={removeSupervisor}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
        <Box sx={{ flexGrow: 1 }}>
          <ReviewConceptAndAttributes addedConcepts={addedConcepts} isLoading={isDataLoading} />
        </Box>

        <Box
          id="review-sidebar"
          sx={{
            width: '450px',
            p: theme.spacing(4),
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(4),
          }}
        >
          <DatasetSummary
            validationEncounterCount={validationEncounterCount}
            testEncounterCount={testEncounterCount}
            trainingEncounterCount={trainingEncounterCount}
            isLoading={isDataLoading}
          />
          <AllocationSummaryTable
            allocationSummary={allocationSummary}
            isLoading={isDataLoading}
            editable={editMode}
            onReallocate={() => setReallocationWizardOpen(true)}
          />
        </Box>
      </Box>
      <ReallocationWizard
        open={isReallocationWizardOpen}
        projectId={projectId}
        onClose={() => setReallocationWizardOpen(false)}
        onConfirm={handleConfirmReallocation}
      />
      <FeedbackSnackbar
        open={Boolean(confirmedToast)}
        message={confirmedToast ?? ''}
        severity={toastFixEnabled ? 'info' : 'success'}
        onClose={() => setConfirmedToast(null)}
      />
    </Box>
  );
};
